import React, { useState, useEffect, useCallback, useRef } from "react";
import { getPlannerByUserId, createPlanner, updatePlanner, deletePlanner } from "../../apis";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import actions from "../../actions";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { FiMoreVertical } from "react-icons/fi";
import "./planner.css";
import Pagination from "@mui/material/Pagination";

const PlannerPage = ({ stopLoadingRequest, startLoadingRequest }) => {
  const [planners, setPlanners] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { id } = useParams(); // user id
  const [selectedPlanner, setSelectedPlanner] = useState("");

  const [fullScreenImage, setFullScreenImage] = useState(null);
  const [isAccess,setIsAccess] = useState(false);
  // Planner form fields
  const [plannerName, setPlannerName] = useState("");
  const [outfitId, setOutfitId] = useState("");
  const [outfitName, setOutfitName] = useState("");
  const [outfitImage, setOutfitImage] = useState(""); // Optional display field
  const [date, setDate] = useState("");

  const [dropdownOpen, setDropdownOpen] = useState(null);
  const dropdownRefs = useRef([]);

  // Pagination and Search States
  const [rowsPerPage, setRowsPerPage] = useState(14);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");

  // Reset current page when search term changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  // Fetch planner data from user details API
  const fetchPlanners = useCallback(async () => {
    try {
      startLoadingRequest();
      const apiResponse = await getPlannerByUserId(id);
      const data = apiResponse?.data?.data?.planners || [];
      const result = [];
      data?.map((item,index)=>{
        if(item?.suggestionDate === new Date()?.toISOString()?.slice(0,10))
        {
          result.unshift(item);
        }else{
          result.push(item);
        }
      })
      setPlanners(result || []);
      setIsAccess(apiResponse?.data?.data?.isAccess)
    } catch (error) {
      setPlanners([]);
    } finally {
      stopLoadingRequest();
    }
  }, [startLoadingRequest, stopLoadingRequest, id]);

  useEffect(() => {
    fetchPlanners();
  }, [fetchPlanners]);

  const handleAddPlanner = () => {
    setSelectedPlanner("");
    setPlannerName("");
    setOutfitId("");
    setOutfitName("");
    setOutfitImage("");
    setDate("");
    setIsModalOpen(true);
  };

  const handleEditPlanner = (planner) => {
    setSelectedPlanner(planner._id);
    setPlannerName(planner.name);
    if (planner.outfitId) {
      setOutfitId(planner.outfitId._id);
      setOutfitName(planner.outfitId.name);
      setOutfitImage(planner.outfitId.image);
    } else {
      setOutfitId("");
      setOutfitName("");
      setOutfitImage("");
    }
    setDate(planner.date); // date should be in a format acceptable by <input type="date">
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const createNewPlanner = async () => {
    try {
      const formData = {
        userId: id,
        name: plannerName,
        outfitId, // Send as string or an object as required by your API
        date,     // ISO date string from <input type="date">
      };
      const response = await createPlanner(formData);
      if (response && response.data) {
        await fetchPlanners();
        toast.success("Planner created successfully!");
        closeModal();
      } else {
        throw new Error("Invalid response from server");
      }
    } catch (error) {
      toast.error("Error creating planner. Please try again.");
    } finally {
      stopLoadingRequest();
    }
  };

  const updateExistingPlanner = async () => {
    try {
      const formData = {
        userId: id,
        plannerId: selectedPlanner,
        name: plannerName,
        outfitId,
        date,
      };
      const response = await updatePlanner(formData);
      if (response && response.data) {
        await fetchPlanners();
        toast.success("Planner updated successfully!");
        closeModal();
      } else {
        throw new Error("Invalid response from server");
      }
    } catch (error) {
      toast.error("Error updating planner. Please try again.");
    } finally {
      stopLoadingRequest();
    }
  };

  const handleDeletePlanner = async (plannerId) => {
    try {
      if (window.confirm("Are you sure you want to delete this planner?")) {
        const response = await deletePlanner(plannerId, id);
        if (response && response.data) {
          await fetchPlanners();
          toast.success("Planner deleted successfully!");
        } else {
          throw new Error("Invalid response from server");
        }
      }
    } catch (error) {
      toast.error("Error deleting planner. Please try again.");
    } finally {
      stopLoadingRequest();
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    startLoadingRequest();
    if (selectedPlanner) {
      updateExistingPlanner();
    } else {
      createNewPlanner();
    }
  };

  const toggleDropdown = (index) => {
    setDropdownOpen(dropdownOpen === index ? null : index);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRefs.current &&
        !dropdownRefs.current.some((ref) => ref && ref.contains(event.target))
      ) {
        setDropdownOpen(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);


  // Filter planners based on search term (searching by planner name and outfit name)
  const filteredPlanners = planners.filter((item) =>
    (item.name || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
    (item.outfitId && (item.name || "").toLowerCase().includes(searchTerm.toLowerCase()))
  );

  // Pagination helper: slice items based on currentPage and rowsPerPage
  const paginateItems = (items) => {
    const indexOfLastItem = currentPage * rowsPerPage;
    const indexOfFirstItem = indexOfLastItem - rowsPerPage;
    return items.slice(indexOfFirstItem, indexOfLastItem);
  };

  const computedTotalPages = Math.ceil(filteredPlanners.length / rowsPerPage);
  const displayedPlanners = paginateItems(filteredPlanners);


  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  return (
    <div className="planner-page">
      <div
        className="table-header"
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "10px",
          backgroundColor:"#ffffff"
        }}
      >
        <h2>
          {searchTerm.trim() === ""
            ? "Planner Items"
            : filteredPlanners.length > 0
            ? <>Results for <span className="highlight">{searchTerm}</span></>
            : "0 Planners Found!"}
        </h2>
        <div className="search-container">
          <input
            type="text"
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>
      {isAccess ? <div className="table-container">
        <table className="planner-table" id="planner-table-color">
          <thead>
            <tr>
              <th>#</th>
              <th>Date</th>
              <th>Item Image 1</th>
              <th>Item Name 1</th>
              <th>Item Image 2</th>
              <th>Item Name 2</th>
              <th>Item Image 3</th>
              <th>Item Name 3</th>
              {/* <th>Actions</th> */}
            </tr>
          </thead>
         <tbody>
  {displayedPlanners?.map((item, index) => {
    const outfits = item.outfits || [];

    // Find outerwear
    const outerwear = outfits.find(
      o => o.category?.name?.toLowerCase() === "outerwear"
    );

    // All other categories
    const nonOuterwear = outfits.filter(
      o => o.category?.name?.toLowerCase() !== "outerwear"
    );

    // Column assignment
    const col1 = nonOuterwear[0] || null;
    const col2 = nonOuterwear[1] || null;
    const col3 = outerwear || nonOuterwear[2] || null;

    const isToday =
      item?.suggestionDate === new Date().toISOString().slice(0, 10);

    const bg = isToday ? "#7575754D" : "";

    return (
      <tr key={item.suggestionDate}>
        {/* Serial */}
        <td style={{ background: bg }}>
          {(currentPage - 1) * rowsPerPage + index + 1}
        </td>

        {/* Date */}
        <td style={{ background: bg }}>{item.suggestionDate}</td>

        {/* COLUMN 1 */}
        <td style={{ background: bg }}>
          {col1?.image && (
            <img
              src={col1.image}
              alt={col1.name}
              className="planner-outfit-image"
              onClick={() => setFullScreenImage(col1.image)}
            />
          )}
        </td>
        <td style={{ background: bg }}>{col1?.name || "N/A"}</td>

        {/* COLUMN 2 */}
        <td style={{ background: bg }}>
          {col2?.image && (
            <img
              src={col2.image}
              alt={col2.name}
              className="planner-outfit-image"
              onClick={() => setFullScreenImage(col2.image)}
            />
          )}
        </td>
        <td style={{ background: bg }}>{col2?.name || "N/A"}</td>

        {/* COLUMN 3 (OUTERWEAR PRIORITY) */}
        <td style={{ background: bg }}>
          {col3?.image && (
            <img
              src={col3.image}
              alt={col3.name}
              className="planner-outfit-image"
              onClick={() => setFullScreenImage(col3.image)}
            />
          )}
        </td>
        <td style={{ background: bg }}>{col3?.name || "N/A"}</td>
      </tr>
    );
  })}
</tbody>

        </table>
        <div className="pagination-container">
          <Pagination
            count={computedTotalPages}
            page={currentPage}
            onChange={handlePageChange}
            color="primary"
          />
        </div>
      </div> : 
      <div style={{width:"100%",height:"10rem", color:"red", textAlign:"center",fontSize:"15px",display:"flex",alignItems:"center",justifyContent:"center"}}>You have no permission to access user planner</div>
      }
      {fullScreenImage && (
        <div className="full-screen-overlay" onClick={() => setFullScreenImage(null)}>
          <img src={fullScreenImage} alt="Full Screen" className="full-screen-image" />
        </div>
      )}
      {isModalOpen && (
        <div className="modal-overlay">
          <div className="modal-planner-content">
            <div className="modal-header">
              <h2>{selectedPlanner ? "Edit Planner" : "Add New Planner"}</h2>
              <span className="close" onClick={closeModal}>
                &times;
              </span>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="plannerName">Planner Name</label>
                <input
                  type="text"
                  id="plannerName"
                  name="plannerName"
                  required
                  value={plannerName}
                  onChange={(e) => setPlannerName(e.target.value)}
                />
              </div>
              <div className="form-group">
                <label htmlFor="outfitId">Outfit Name</label>
                <input
                  type="text"
                  id="outfitId"
                  name="outfitId"
                  required
                  value={outfitName}
                  onChange={(e) => setOutfitName(e.target.value)}
                />
              </div>
              <div className="form-group">
                <label htmlFor="date">Date</label>
                <input
                  type="date"
                  id="date"
                  name="date"
                  required
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                />
              </div>
              <div className="modal-actions">
                <button type="button" className="cancel-button" onClick={closeModal}>
                  Cancel
                </button>
                <button type="submit" className="submit-button">
                  Save
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      stopLoadingRequest: actions.loader.stopLoading,
      startLoadingRequest: actions.loader.startLoading,
    },
    dispatch
  );

export default connect(null, mapDispatchToProps)(PlannerPage);
